import clsx from 'clsx'
import { Children, ComponentProps } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { Icon, IconNameAndCategoryString } from '../Icon'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import Tag from '../app/design/Tag'

type Props = {
    title: string
    icon: IconNameAndCategoryString | JSX.Element
    description?: string
    descriptionClassName?: string
    secondaryText?: string
    secondaryTextClassName?: string
    thirdLine?: React.ReactNode
    children?: React.ReactNode
    secondaryView?: React.ReactNode
    hasHoverEffect?: boolean
    hasReducedHoverEffect?: boolean
    infoTag?: string
    tagIntent?: ComponentProps<typeof Tag>['intent']
    className?: string
    disabled?: boolean
}

export const WorkflowIconSection = ({
    title,
    icon,
    description,
    descriptionClassName,
    secondaryText,
    secondaryTextClassName,
    thirdLine,
    children,
    secondaryView,
    hasHoverEffect = false,
    hasReducedHoverEffect = false,
    infoTag,
    tagIntent = 'info',
    className,
    disabled = false,
}: Props) => {
    const shouldPutChildrenInOwnCell = description !== undefined || secondaryView !== undefined
    const shouldCenterTitle = !children || shouldPutChildrenInOwnCell

    const HeaderContainerElement = shouldPutChildrenInOwnCell ? 'label' : 'div'

    // We only want to render a second grid row in this component's layout if there
    // are children with content to render. Unfortunately, we can't just check the
    // `children` prop directly because `null` children will show up in that prop.
    // So here we use `Children.map` to explicitly check for content.
    const doChildrenHaveContent =
        (!!children && Children.map(children, (child) => !!child)?.some(Boolean)) ?? false

    return (
        <div
            className={twm(
                'z-10 grid grid-cols-[min-content_1fr] gap-x-4 gap-y-3',
                shouldPutChildrenInOwnCell && doChildrenHaveContent
                    ? 'grid-rows-[min-content_1fr]'
                    : 'grid-rows-[min-content]',
                className
            )}
        >
            <div
                className={twm(
                    'relative col-span-2 grid cursor-auto grid-cols-subgrid',
                    hasHoverEffect &&
                        !disabled &&
                        '-m-4 cursor-pointer rounded-lg p-4 hover:bg-black/4 active:bg-black/8',
                    hasReducedHoverEffect &&
                        !disabled &&
                        '-m-4 -mb-3 cursor-pointer rounded-lg p-4 pb-3 hover:bg-black/4 active:bg-black/8'
                )}
            >
                <div
                    className={twm(
                        'pointer-events-none flex h-full items-center justify-center',
                        disabled && 'opacity-50'
                    )}
                >
                    {typeof icon === 'string' ? (
                        <Icon
                            icon={icon as IconNameAndCategoryString}
                            background="round"
                            className="shrink-0"
                        />
                    ) : (
                        icon
                    )}
                </div>

                <HeaderContainerElement
                    className={twm(
                        'flex min-w-0 items-stretch justify-between',
                        !shouldPutChildrenInOwnCell && 'row-span-2'
                    )}
                >
                    <div
                        className={twm(
                            'flex min-w-0 grow flex-col items-stretch gap-y-3',
                            shouldCenterTitle ? 'justify-center' : 'justify-start',
                            hasHoverEffect && !disabled && 'cursor-pointer'
                        )}
                    >
                        <div className={clsx('flex flex-col items-stretch justify-start gap-y-1')}>
                            <div className={clsx('flex items-center justify-between gap-x-2')}>
                                <div className="flex items-center gap-2">
                                    <Label className={twm('block', disabled && 'text-gray-400')}>
                                        {title}
                                    </Label>
                                    {infoTag && (
                                        <Tag label={infoTag} intent={tagIntent} size="sm" />
                                    )}
                                </div>
                                {secondaryText !== undefined && (
                                    <Paragraph
                                        size="sm"
                                        className={twm(
                                            'font-normal text-gray-600',
                                            disabled && 'text-gray-400',
                                            secondaryTextClassName
                                        )}
                                    >
                                        {secondaryText}
                                    </Paragraph>
                                )}
                            </div>
                            {description !== undefined && (
                                <Paragraph
                                    className={twm(
                                        'font-normal text-gray-600',
                                        disabled && 'text-gray-400',
                                        descriptionClassName
                                    )}
                                    size="sm"
                                >
                                    {description}
                                </Paragraph>
                            )}
                            {thirdLine !== undefined && (
                                <Label size="sm" className={twm(disabled && 'text-gray-400')}>
                                    {thirdLine}
                                </Label>
                            )}
                        </div>

                        {!shouldPutChildrenInOwnCell && children}
                    </div>

                    {secondaryView && (
                        <div className="ml-4 flex items-center justify-center">{secondaryView}</div>
                    )}
                </HeaderContainerElement>
            </div>

            {shouldPutChildrenInOwnCell && doChildrenHaveContent && (
                <>
                    <div />
                    <div className="px-0">{children}</div>
                </>
            )}
        </div>
    )
}
